import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

/** An arrow fired at a claim element — an authority, a contradiction, or evidence. */
export interface ClaimArrow {
  id: string;
  thrust: string;
  strength: string; // strong | moderate | weak
  arrow_kind: string; // authority | contradiction | evidence
  source_label: string | null;
  contra_paragraph: number | null;
  source_evidence_id: string | null;
  source_authority_id: string | null;
  source_contradiction_id: string | null;
}

/** One element the plaintiff must prove for a claim, with its survival status. */
export interface ClaimElement {
  id: string;
  name: string;
  ordinal: number | null;
  status: string; // intact | contested | defeated | unassessed
  must_prove: string | null;
  rationale: string | null;
  arrows: ClaimArrow[];
}

/** A cause of action with element-by-element survival assessment (the Archers). */
export interface Claim {
  id: string;
  ordinal: number | null;
  claim_name: string;
  target: string | null;
  summary: string | null;
  survival: string; // intact | contested | defeated
  survival_rationale: string | null;
  elements: ClaimElement[];
}

/** All claims for a case with their element/arrow assessment. */
export function useCaseClaims(caseId: string | undefined) {
  return useQuery<Claim[], Error>({
    queryKey: ["case-claims", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await berserkr().rpc("fetch_case_claims", {
        p_case_id: caseId,
      });
      if (error) throw new Error(error.message);
      return (data as Claim[]) ?? [];
    },
  });
}
